import { Component, OnInit } from '@angular/core';
import { FormComponentLookup } from '@ptl/forms';
import { PlansStateStructure } from 'src/app/angular-elements/plan-selection/configs/interfaces/plansStateStructure.interface';
import { Product } from 'src/app/angular-elements/plan-selection/interfaces/product.interface';
import { PlanUtilityService } from 'src/app/angular-elements/plan-selection/plan-summary-list/plan-utility.service';
import { PlanAttributes, SummaryView } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';

import { OnExchangeEligibilityStateStructure, ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME } from 'src/app/angular-elements/on-exchange-enrollment/reducers/eligibility.reducer';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { EnrollManagerService } from 'src/app/angular-elements/on-exchange-enrollment/services/enroll-manager.service';
import { EnrollPresenterService } from 'src/app/angular-elements/on-exchange-enrollment/services/enroll-presenter.service';
import { PersonPresenterService } from 'src/app/angular-elements/on-exchange-enrollment/services/person-presenter.service';

interface ApplicantRenderable {
  fullName: string;
  gender: string;
  dateOfBirth: string;
  address: string;
  county: string;
  tobaccoUse: string;
  lastSmokingDate: string;
}
interface FamilyMemberRenderable extends ApplicantRenderable {
  relationshipToPrimaryApplicant: string;
}

@FormComponentLookup('marketplace-review-application')
@Component({
    selector: 'app-marketplace-review-application',
    templateUrl: './marketplace-review-application.component.html',
    styleUrls: ['./marketplace-review-application.component.scss'],
    standalone: false
})
export class MarketplaceReviewApplicationComponent implements OnInit {

  selectedPlan: Product<PlanAttributes, SummaryView> = null;
  effectiveDate: string;
  contactInfo = {
    fullName: null,
    address: null,
    phoneNumber: null,
    emailAddress: null
  };
  primaryInfo: ApplicantRenderable = null;
  familyInfo: FamilyMemberRenderable[] = null;

  /** APTC Info */
  selectedAPTCToolTipContent: string;
  appliedAPTCToolTipContent: string;

  selectedAPTC: string;
  appliedAPTC: string;
  individualResponsibilityAmount: string;
  totalPremiumAmount: string;
  effectiveDateYear: string;

  constructor(
    private planUtilityService: PlanUtilityService,
    private reducerUtilityService: ReducerUtilityService,
    private enrollManagerService: EnrollManagerService,
    private enrollPresenterService: EnrollPresenterService,
    private personPresenterService: PersonPresenterService
  ) { }

  ngOnInit(): void {

    const eligibilityState: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const matchedProspects = eligibilityState.prospects.filter(p => eligibilityState.currentProspectId === p.prospectId );

    if (matchedProspects.length > 0) {
      const prospect = matchedProspects[0];
      this.effectiveDate = prospect.data.effectiveDate;

      /**
       * We don't want to trust that the user has visited view-plans before landing here
       * ( as would be the case with a follow-up app-status check )
       */
      this.selectedPlan = prospect.data.selectedPlan;

      this.setupContactInfo();
      this.setupPrimaryInfo();
      this.setupFamilyInfo();
      this.setupAPTCInfo();
    }

  }

  setupContactInfo() {
    this.contactInfo = {
      fullName: this.enrollPresenterService.onXcontractFullName(),
      address: this.enrollPresenterService.contractAddressOnX(),
      phoneNumber: this.enrollPresenterService.contractPhoneNumber('primary'),
      emailAddress: this.enrollPresenterService.contractEmail()
    };
  }
  setupPrimaryInfo() {
    const primary = this.enrollManagerService.getPrimaryApplicantInformation();
    this.primaryInfo = {
      fullName: this.personPresenterService.fullName(primary.id),
      gender: this.personPresenterService.gender(primary.id),
      dateOfBirth: this.personPresenterService.dateOfBirth(primary.id),
      address: this.personPresenterService.addressOnX(primary.id),
      county: primary.homeAddress.county,
      tobaccoUse: this.personPresenterService.tobaccoUseOnX(primary.id),
      lastSmokingDate: this.personPresenterService.tobaccoDate(primary.id)
    };
  }
  setupFamilyInfo() {
    const familyInformation = this.enrollManagerService.getAdditionalApplicantInformation();
    if (familyInformation.length > 0) {
      this.familyInfo = [];

      const primary = this.enrollManagerService.getPrimaryApplicantInformation();

      familyInformation.forEach( p => {
        const familyMember = {
          fullName: this.enrollPresenterService.familyFullName(p.id),
          gender: this.enrollPresenterService.familyGender(p.id),
          dateOfBirth: this.enrollPresenterService.familyDateOfBirth(p.id),
          address: this.enrollPresenterService.familyAddressOnX(p.id),
          county: p.homeAddress.county,
          tobaccoUse: this.personPresenterService.tobaccoUseOnX(p.id),
          lastSmokingDate: this.personPresenterService.tobaccoDate(p.id),
          relationshipToPrimaryApplicant: this.enrollPresenterService.relationshipDecoder(primary, p)
        };

        this.familyInfo.push(familyMember);
      });
    }
  }
  setupAPTCInfo() {
    const planState: PlansStateStructure = this.reducerUtilityService.getComponentStates()['Plans'];
    const plansFromState = this.planUtilityService.getPlansByProductGroupAsArray();
    const selectedPlan: Product<PlanAttributes, SummaryView>
      = plansFromState.filter(plan => plan.attributes.PLAN_ID === planState.planIdForEnrollment)[0];

    const eligibilityState: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const matchedProspects = eligibilityState.prospects.filter(p => eligibilityState.currentProspectId === p.prospectId );

    if (matchedProspects.length > 0) {
      const prospect = matchedProspects[0];

      const splitEffectiveDate = prospect.data.effectiveDate.split('/');
      this.effectiveDateYear = splitEffectiveDate[splitEffectiveDate.length - 1];

      this.selectedAPTC = prospect.selectedMonthlyAptcForProspect.toFixed(2);
      this.appliedAPTC = prospect.appliedAPTC.toFixed(2);
      this.individualResponsibilityAmount = prospect.individualResponsibilityAmount;
      this.totalPremiumAmount = selectedPlan.price as string;


      this.selectedAPTCToolTipContent = 'This is the amount of Advanced Premium Tax Credit (APTC) this applicant has chosen to apply right away, to help lower monthly insurance premium costs. Enrollees can choose to apply some or all of their APTC to their health plan monthly premium. They will receive any APTC amount leftover at the end of the year, when they file their annual federal income tax return.';
      this.appliedAPTCToolTipContent = 'This is the amount of Advanced Premium Tax Credit (APTC) that may be used to offset monthly insurance premium costs. Enrollees can choose to apply some or all of their APTC to their health plan monthly premium. They will receive any APTC amount leftover at the end of the year, when they file their annual federal income tax return.';
    }
  }


  trackByIndex(index: number, familyMember: FamilyMemberRenderable): number {
    return index;
  }

}
